import { useEffect, useState } from 'react';
import { api, send } from './api.js';
import { Notice } from './components.js';
import { t, localeTag } from './i18n.js';
import { browserZone, exactLocalToIso } from './time.js';
import { localDate, commonSlots } from '../shared/team-calendar.js';
type Member = { id: string; name: string; email: string; source: string };
type Result = { userId: string; shared: boolean; busy: { start: string; end: string }[] };
export function TeamAvailability({
  people,
  onChoose,
}: {
  people: Member[];
  onChoose: (start: string, end: string, ids: string[]) => void;
}) {
  const [shared, setShared] = useState(false),
    [ids, setIds] = useState<string[]>([]),
    [date, setDate] = useState(localDate(new Date())),
    [minutes, setMinutes] = useState(60),
    [result, setResult] = useState<Result[] | null>(null),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    void api('/collaboration/capabilities')
      .then((c) => {
        if (live) setShared(c.availabilityShared);
      })
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, []);
  const invalidate = () => {
    setResult(null);
    setError('');
  };
  const time = (v: string) =>
    new Date(v).toLocaleTimeString(localeTag(), { hour: '2-digit', minute: '2-digit' });
  const from = exactLocalToIso(date + 'T08:00', browserZone()),
    to = exactLocalToIso(date + 'T18:00', browserZone());
  const allShared = result?.length && result.every((r) => r.shared);
  const slots =
    allShared && from && to
      ? commonSlots(
          from,
          to,
          minutes,
          result!.flatMap((r) => r.busy),
        )
      : [];
  return (
    <section className="pro-card team-availability">
      <h2>{t('Disponibilités de l’équipe')}</h2>
      <p>
        {t(
          'Partagez uniquement vos créneaux occupés. Les titres et descriptions de votre agenda personnel restent privés.',
        )}
      </p>
      <label className="team-check">
        <input
          type="checkbox"
          checked={shared}
          disabled={busy}
          onChange={(e) => {
            const enabled = e.target.checked;
            setShared(enabled);
            setResult(null);
            setBusy(true);
            setError('');
            void send('/collaboration/availability/preference', { enabled }, 'PUT')
              .then(() => {
                setShared(enabled);
                setResult(null);
              })
              .catch((e) => {
                setShared(!enabled);
                setError(e.message);
              })
              .finally(() => setBusy(false));
          }}
        />
        {t('Partager mes disponibilités avec mon organisation')}
      </label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!ids.length) return;
          setBusy(true);
          setError('');
          setResult(null);
          const start = exactLocalToIso(date + 'T00:00', browserZone());
          const next = new Date(date + 'T12:00:00');
          next.setDate(next.getDate() + 1);
          const end = exactLocalToIso(localDate(next) + 'T00:00', browserZone());
          if (!start || !end) {
            setError(t('Date invalide.'));
            setBusy(false);
            return;
          }
          void send('/collaboration/availability', { userIds: ids, from: start, to: end })
            .then(setResult)
            .catch((e) => setError(e.message))
            .finally(() => setBusy(false));
        }}
      >
        <fieldset>
          <legend>{t('Membres à réunir (20 maximum)')}</legend>
          <div className="team-member-options">
            {people
              .filter((p) => p.source === 'member')
              .map((p) => (
                <label className="team-check" key={p.id}>
                  <input
                    type="checkbox"
                    checked={ids.includes(p.id)}
                    disabled={busy || (!ids.includes(p.id) && ids.length === 20)}
                    onChange={(e) => {
                      setIds(e.target.checked ? [...ids, p.id] : ids.filter((id) => id !== p.id));
                      invalidate();
                    }}
                  />
                  {p.name}
                </label>
              ))}
          </div>
        </fieldset>
        <div className="pro-form">
          <label>
            {t('Jour de la réunion')}
            <input
              required
              type="date"
              value={date}
              disabled={busy}
              onChange={(e) => {
                setDate(e.target.value);
                invalidate();
              }}
            />
          </label>
          <label>
            {t('Durée de la réunion')}
            <select
              value={minutes}
              disabled={busy}
              onChange={(e) => {
                setMinutes(Number(e.target.value));
                invalidate();
              }}
            >
              {[30, 60, 90, 120].map((m) => (
                <option value={m} key={m}>
                  {t('{minutes} minutes', { minutes: m })}
                </option>
              ))}
            </select>
          </label>
          <button disabled={busy || !ids.length}>{t('Chercher des créneaux')}</button>
        </div>
      </form>
      {busy && <p role="status">{t('Traitement en cours…')}</p>}
      {error && <Notice type="error">{error}</Notice>}
      {result && (
        <div aria-live="polite">
          {result.map((r) => (
            <p key={r.userId}>
              <strong>{people.find((p) => p.id === r.userId)?.name}</strong> :{' '}
              {!r.shared
                ? t('Disponibilités non partagées')
                : r.busy.length
                  ? r.busy.map((b) => `${time(b.start)}–${time(b.end)}`).join(' · ')
                  : t('Aucun créneau occupé ce jour')}
            </p>
          ))}
          {!allShared ? (
            <Notice>
              {t(
                'Tous les membres sélectionnés doivent partager leurs disponibilités pour proposer un créneau commun.',
              )}
            </Notice>
          ) : (
            <>
              <h3>{t('Créneaux communs entre 08 h et 18 h')}</h3>
              <p>
                {t(
                  'Heures affichées dans votre fuseau local. Ces propositions ne réservent pas les agendas.',
                )}
              </p>
              {!slots.length && <p>{t('Aucun créneau commun pour cette durée.')}</p>}
              <div className="team-slot-options">
                {slots.slice(0, 20).map((s) => (
                  <button type="button" key={s.start} onClick={() => onChoose(s.start, s.end, ids)}>
                    {time(s.start)}–{time(s.end)}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
